// @ts-strict-ignore
import type { InMemoryCache, FetchResult } from "@apollo/client";
import { useState } from "react";

import { useToast } from "@aviary";
import { MapAgreementModal } from "@emerson/components/MapAgreementModal/MapAgreementModal";
import type { WholesaleCartAddToCartData } from "@shared/data/mutations/WholesaleCartAddToCartMutation/WholesaleCartAddToCart.mutation";
import { __private_useWholesaleCartAddToCart__ } from "@shared/data/mutations/WholesaleCartAddToCartMutation/WholesaleCartAddToCart.mutation";
import type { WholesaleCartQueryData } from "@shared/data/queries/WholesaleCartQuery/WholesaleCart.query";
import { WholesaleCart_Shared_Query } from "@shared/data/queries/WholesaleCartQuery/WholesaleCart.query";
import type { BrandAgreementEntryPoint } from "@shared/hooks/useAnalyticsLogger/StoreAnalyticsEntryPoint";

const useAddToCartWholesale = (
  onResolvedMapAgreement: () => void,
  entryPoint: BrandAgreementEntryPoint,
  onCompleted?: () => void
) => {
  const { makeToast } = useToast();
  const [mapPolicyIdToAgree, setMapPolicyIdToAgree] = useState<string>("");

  const updateWholesaleCart = (
    cache: InMemoryCache,
    mutationResult: FetchResult<WholesaleCartAddToCartData>
  ) => {
    const data: WholesaleCartQueryData = cache.readQuery({
      query: WholesaleCart_Shared_Query,
    });
    if (data.currentOffice.wholesaleCart) {
      return;
    }

    const currentOffice = {
      ...data.currentOffice,
      wholesaleCart: mutationResult.data.wholesaleCartAddToCart.cart,
    };
    cache.writeQuery({
      query: WholesaleCart_Shared_Query,
      data: { ...data, currentOffice },
    });
  };

  const [wholesaleAddToCart, { loading }] = __private_useWholesaleCartAddToCart__({
    onCompleted: addToCardData => {
      const errors = addToCardData.wholesaleCartAddToCart?.errors;
      if (errors?.fields?.mapAgreements?.length > 0) {
        setMapPolicyIdToAgree(errors?.fields?.mapAgreements[0]);
      } else if (errors?.message) {
        makeToast("error", errors?.message);
      } else {
        setMapPolicyIdToAgree("");
        if (onCompleted) {
          onCompleted();
        }
      }
    },
    update: updateWholesaleCart,
    onError: error => makeToast("error", error.message),
  });

  const renderMapAgreementModal = () => {
    if (mapPolicyIdToAgree) {
      return (
        <MapAgreementModal
          isOpen
          mapAgreementId={mapPolicyIdToAgree}
          onResolved={onResolvedMapAgreement}
          onRejected={() => setMapPolicyIdToAgree("")}
          entryPoint={entryPoint}
        />
      );
    }
  };

  return { mapPolicyIdToAgree, wholesaleAddToCart, loading, renderMapAgreementModal };
};

export { useAddToCartWholesale };
